import React from 'react';
import clsx from 'clsx';
import { ElementCount } from './types.ts';
import SvgMail from '@icons/mail.svg?react';
import SvgBell from '@icons/bell-inverted.svg?react';

export const isElementCountObject = (v: unknown): v is ElementCount =>
  v !== null &&
  typeof v === 'object' &&
  (('userMessage' in (v as any)) || ('systemMessage' in (v as any)) || ('count' in (v as any)) || ('errorCount' in (v as any)) || ('warningCount' in (v as any)));

export const hasVisibleBadge = (elementCount: unknown): elementCount is ElementCount =>
  isElementCountObject(elementCount) &&
  ((elementCount.count ?? 0) > 0 ||
    (elementCount.userMessage ?? 0) > 0 ||
    (elementCount.systemMessage ?? 0) > 0 ||
    (elementCount.errorCount ?? 0) > 0 ||
    (elementCount.warningCount ?? 0) > 0);

const cap = (n?: number) => {
  if (!n || n <= 0) return 0;
  if (n > 99) return '99+';
  return n;
};

/**
 * The pills a menu row can carry: the original brand pill (count / mail / bell segments) plus the
 * jobs-center pair — yellow "check me" (done with failed items) and red "needs attention" (error).
 * Shared by MenuItem (group rows) and SubMenuItem (leaf rows) so both render identically.
 */
/**
 * `compact` is the collapsed rail: 80px wide with the icon taking the middle of it, so a pill with
 * two digits — let alone a count beside a mail and a bell — cannot lie across the row. Compact
 * stacks the segments instead of widening, and tightens the padding to match.
 */
const MenuBadges: React.FC<{ elementCount: ElementCount; compact?: boolean }> = ({ elementCount, compact = false }) => (
  <>
    {((elementCount.count ?? 0) > 0 || (elementCount.userMessage ?? 0) > 0 || (elementCount.systemMessage ?? 0) > 0) && (
      <div
        className={clsx('flex items-center justify-center bg-content-brand text-white rounded-full', {
          'px-2 gap-2 text-xs': !compact,
          'flex-col px-1 py-0.5 gap-0.5 text-[10px] leading-none': compact,
        })}
      >
        {(elementCount.count ?? 0) > 0 && (
          <div className="flex items-center py-1">
            <span className="leading-none whitespace-nowrap">{cap(elementCount.count)}</span>
          </div>
        )}
        {(elementCount.userMessage ?? 0) > 0 && (
          <div className="flex items-center gap-1">
            <SvgMail className="w-3 h-3 flex-shrink-0" />
            <span className="leading-none whitespace-nowrap">{cap(elementCount.userMessage)}</span>
          </div>
        )}
        {(elementCount.systemMessage ?? 0) > 0 && (
          <div className="flex items-center gap-1">
            <SvgBell className="w-3 h-3 flex-shrink-0" />
            <span className="leading-none whitespace-nowrap">{cap(elementCount.systemMessage)}</span>
          </div>
        )}
      </div>
    )}
    {(elementCount.warningCount ?? 0) > 0 && (
      <div
        className={clsx('flex items-center justify-center bg-complementary-warning text-content-primary rounded-full', {
          'px-2 py-1 text-xs': !compact,
          'px-1 py-0.5 text-[10px] leading-none': compact,
        })}
      >
        <span className="leading-none whitespace-nowrap">{cap(elementCount.warningCount)}</span>
      </div>
    )}
    {(elementCount.errorCount ?? 0) > 0 && (
      <div
        className={clsx('flex items-center justify-center bg-complementary-error text-white rounded-full', {
          'px-2 py-1 text-xs': !compact,
          'px-1 py-0.5 text-[10px] leading-none': compact,
        })}
      >
        <span className="leading-none whitespace-nowrap">{cap(elementCount.errorCount)}</span>
      </div>
    )}
  </>
);

export default MenuBadges;
